<template>
  <header class="app-header">
    <div class="header-container">
      <!-- Left side - Brand -->
      <div class="header__left">
        <button v-if="showSidebarToggle" class="sidebar-toggle" @click="toggleMobileSidebar">
          <font-awesome-icon icon="fa-solid fa-bars" />
        </button>

        <div class="header__brand">
          <span class="brand-text"></span>
        </div>
      </div>

      <!-- Right side - User Info -->
      <div class="header__right">
        <slot name="header-actions">
          <!-- Feature modules can add their own header actions here -->
        </slot>

        <!-- User Profile Dropdown -->
        <div class="user-profile-dropdown" ref="userDropdown">
          <button class="user-info" @click="toggleDropdown">
            <span class="user-name">{{ userName }}</span>
            <div class="user-avatar">
              <font-awesome-icon icon="fa-solid fa-user" />
            </div>
            <font-awesome-icon icon="fa-solid fa-chevron-down" class="dropdown-arrow"
              :class="{ 'dropdown-arrow--open': isDropdownOpen }" />
          </button>

          <!-- Dropdown Menu -->
          <div v-if="isDropdownOpen" class="dropdown-menu">
            <!-- User Info Section -->
            <div class="dropdown-user-info">
              <div class="dropdown-user-avatar">
                <font-awesome-icon icon="fa-solid fa-user" />
              </div>
              <div class="dropdown-user-details">
                <div class="dropdown-user-name">{{ userName }}</div>
                <div class="dropdown-user-role">{{ userRole }}</div>
              </div>
            </div>

            <!-- Menu Items -->
            <div class="dropdown-menu-items">
              <button class="dropdown-item" :class="{ 'dropdown-item--active': isProfileActive }"
                @click="navigateToProfile">
                <font-awesome-icon icon="fa-solid fa-user" class="dropdown-item-icon" />
                Profile
              </button>
            </div>

            <!-- Logout Button -->
            <div class="dropdown-logout-section">
              <button class="logout-button" @click="handleLogout">
                <font-awesome-icon icon="fa-solid fa-sign-out-alt" class="logout-icon" />
                Logout
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </header>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'

export default {
  name: 'AppHeader',
  data() {
    return {
      isDropdownOpen: false,
      isMobile: false
    }
  },
  computed: {
    ...mapGetters('auth', ['userName', 'userRole']),

    appName() {
      return import.meta.env.VITE_APP_NAME
    },

    layoutType() {
      return this.$route.meta.layout || 'default'
    },

    showSidebarToggle() {
      return this.isMobile && this.$route.meta.requiresAuth
    },

    isProfileActive() {
      return this.$route.path === '/profile' || this.$route.name === 'UserProfile'
    }
  },
  mounted() {
    // Close dropdown when clicking outside
    document.addEventListener('click', this.handleClickOutside)
    
    // Check for mobile on mount and resize
    this.checkMobile()
    window.addEventListener('resize', this.checkMobile)
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleClickOutside)
    window.removeEventListener('resize', this.checkMobile)
  },
  methods: {
    ...mapActions('app', ['toggleSidebar', 'toggleSidebarMobile']),
    ...mapActions('auth', ['logout']),

    toggleDropdown() {
      this.isDropdownOpen = !this.isDropdownOpen
    },

    closeDropdown() {
      this.isDropdownOpen = false
    },

    handleClickOutside(event) {
      if (this.$refs.userDropdown && !this.$refs.userDropdown.contains(event.target)) {
        this.closeDropdown()
      }
    },

    navigateToProfile() {
      this.closeDropdown()
      this.$router.push('/profile')
    },

    async handleLogout() {
      this.closeDropdown()
      try {
        await this.logout()
        this.$router.push('/login')
      } catch (error) {
        console.error('Logout failed:', error)
      }
    },

    toggleMobileSidebar() {
      this.toggleSidebarMobile()
    },

    checkMobile() {
      this.isMobile = window.innerWidth <= 768   // must match the CSS: @media (max-width: 768px)
    }
  }
}
</script>

<style scoped>
.app-header {
  grid-area: header;
  background-color: #ffffff;
  border-bottom: 1px solid #f5f5f5;
  position: sticky;
  top: 0;
  z-index: 100;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

.header-container {
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 30px;
  width: 100%;
}

.header__left {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.sidebar-toggle {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
  border: none;
  background: none;
  color: #6b7280;
  border-radius: 0.375rem;
  cursor: pointer;
  transition: all 0.15s ease-in-out;
}

.sidebar-toggle:hover {
  background-color: #f3f4f6;
  color: #374151;
}

.header__brand {
  display: flex;
  align-items: center;
}

.brand-text {
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  font-weight: 700;
  font-size: 20px;
  color: #333333;
  letter-spacing: 0;
  line-height: 24px;
}

.header__right {
  display: flex;
  align-items: center;
  gap: 1rem;
}

/* User Profile Dropdown Styles */
.user-profile-dropdown {
  position: relative;
}

.user-info {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border: none;
  background: none;
  cursor: pointer;
  border-radius: 8px;
  transition: background-color 0.15s ease-in-out;
}

.user-info:hover {
  background-color: #f9fafb;
}

.user-name {
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  font-weight: 500;
  font-size: 14px;
  color: #333333;
  letter-spacing: 0;
  line-height: 17px;
}

.user-avatar {
  width: 32px;
  height: 32px;
  background-color: #f5f5f5;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9ca3af;
  font-size: 14px;
}

.dropdown-arrow {
  font-size: 12px;
  color: #6b7280;
  transition: transform 0.15s ease-in-out;
}

.dropdown-arrow--open {
  transform: rotate(180deg);
}

/* Dropdown Menu */
.dropdown-menu {
  position: absolute;
  top: 100%;
  right: 0;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
  width: 240px;
  margin-top: 8px;
  z-index: 1000;
  overflow: hidden;
}

/* User Info in Dropdown */
.dropdown-user-info {
  padding: 16px;
  border-bottom: 1px solid #f3f4f6;
  display: flex;
  align-items: center;
  gap: 12px;
  background-color: #fafafa;
}

.dropdown-user-avatar {
  width: 40px;
  height: 40px;
  background-color: #e5e7eb;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
  font-size: 16px;
}

.dropdown-user-details {
  flex: 1;
}

.dropdown-user-name {
  font-weight: 600;
  font-size: 14px;
  color: #111827;
  margin-bottom: 2px;
}

.dropdown-user-role {
  font-size: 12px;
  color: #6b7280;
}

/* Menu Items */
.dropdown-menu-items {
  padding: 8px 0;
}

.dropdown-item {
  width: 100%;
  padding: 12px 16px;
  border: none;
  background: none;
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  transition: background-color 0.15s ease-in-out;
  font-size: 14px;
  color: #374151;
  text-align: left;
  position: relative;
}

.dropdown-item:hover {
  background-color: #f9fafb;
}

.dropdown-item--active {
  background-color: rgba(74, 143, 227, 0.1);
  color: #4a8fe3;
  border-left: 3px solid #4a8fe3;
  border-right: 3px solid transparent;
  font-weight: 600;
}

.dropdown-item--active:hover {
  background-color: rgba(74, 143, 227, 0.15);
}

.dropdown-item--active .dropdown-item-icon {
  color: #4a8fe3;
}

.dropdown-item-icon {
  width: 16px;
  color: #6b7280;
}

/* Logout Section */
.dropdown-logout-section {
  border-top: 1px solid #f3f4f6;
  padding: 8px;
}

.logout-button {
  width: 100%;
  padding: 10px 16px;
  border: none;
  background-color: #ef4444;
  color: white;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 500;
  transition: background-color 0.15s ease-in-out;
}

.logout-button:hover {
  background-color: #dc2626;
}

.logout-icon {
  font-size: 14px;
}

@media (max-width: 768px) {
  .header-container {
    padding: 0 16px;
  }

  .user-name {
    display: none;
  }

  .dropdown-menu {
    width: 200px;
  }
}
</style>